/**
 * Floating inline-formatting bar that appears over a non-empty text
 * selection in the markdown editor. Auto-positions above the selection via
 * Tiptap's `BubbleMenu` (Floating UI under the hood) — no toolbar button to
 * hunt for.
 *
 * Adapted from Bear's `packages/app/src/components/MarkdownLinkPopover.tsx`,
 * with two changes:
 *   - It's a `BubbleMenu` (pops up on highlight) rather than a toolbar-button
 *     popover, and it carries bold/italic/strike/inline-code toggles
 *     alongside the link control.
 *   - Reactive active-state comes from `useEditorState` instead of a manually
 *     threaded `viewState` prop.
 *
 * Kept shadcn-free to match the rest of this package (see EditorToolbar): raw
 * `<button>`s + tailwind so forest stays consumer-agnostic.
 */
import type { Editor } from "@tiptap/react";
import { useEditorState } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import {
  Bold,
  Code,
  Italic,
  Link2,
  MessageSquarePlus,
  Strikethrough,
} from "lucide-react";
import { type KeyboardEvent, useEffect, useRef, useState } from "react";

import {
  commentOnSelection,
  type MarkdownCommentSelection,
} from "./EditorToolbar";

interface InlineFormatBubbleProps {
  editor: Editor;
  /** Offers a Comment button on the selection when set. */
  onCommentSelection?: (selection: MarkdownCommentSelection) => void;
}

export function InlineFormatBubble({
  editor,
  onCommentSelection,
}: InlineFormatBubbleProps) {
  // Whether the bar is in link-editing mode (URL input shown instead of the
  // format toggles). A ref mirror is read from `shouldShow`, which runs
  // outside React's render, to keep the bar open while the input is focused
  // (focusing the input blurs the editor, which would otherwise hide it).
  const [linkEditing, setLinkEditing] = useState(false);
  const linkEditingRef = useRef(false);
  linkEditingRef.current = linkEditing;
  const [url, setUrl] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const { isBold, isItalic, isStrike, isCode, isLink, selectionKey } =
    useEditorState({
      editor,
      selector: ({ editor }) => {
        if (!editor || editor.isDestroyed) {
          return {
            isBold: false,
            isItalic: false,
            isStrike: false,
            isCode: false,
            isLink: false,
            selectionKey: "",
          };
        }
        const { from, to } = editor.state.selection;
        return {
          isBold: editor.isActive("bold"),
          isItalic: editor.isActive("italic"),
          isStrike: editor.isActive("strike"),
          isCode: editor.isActive("code"),
          isLink: editor.isActive("link"),
          selectionKey: `${from}:${to}`,
        };
      },
    });

  // Any change to the selection range exits link-editing mode: the user has
  // moved on to a different span, so a stale URL input shouldn't linger.
  // Applying a link extends the selection to the mark range, which flips this
  // too — the natural "close after apply".
  useEffect(() => {
    setLinkEditing(false);
  }, [selectionKey]);

  const openLinkEditor = () => {
    const href = editor.getAttributes("link").href;
    setUrl(typeof href === "string" ? href : "");
    setLinkEditing(true);
    // Focus the input after it mounts.
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const applyLink = () => {
    const trimmed = url.trim();
    if (!trimmed) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: trimmed })
        .run();
    }
    setLinkEditing(false);
  };

  const cancelLinkEditor = () => {
    setLinkEditing(false);
    editor.chain().focus().run();
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      applyLink();
    } else if (event.key === "Escape") {
      event.preventDefault();
      cancelLinkEditor();
    }
  };

  return (
    <BubbleMenu
      editor={editor}
      options={{ placement: "top", offset: 8 }}
      shouldShow={({ editor, from, to }) => {
        if (!editor.isEditable) return false;
        // Keep the bar up while the URL input is focused (editor is blurred).
        if (linkEditingRef.current) return true;
        if (from === to) return false; // empty selection — nothing to format
        // Formatting marks don't apply inside code blocks.
        if (editor.isActive("codeBlock")) return false;
        return true;
      }}
      className="flex items-center gap-1 rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md"
    >
      {linkEditing ? (
        <input
          ref={inputRef}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={handleInputKeyDown}
          placeholder="https://example.com"
          className="h-7 w-56 rounded bg-transparent px-2 text-sm outline-none placeholder:text-muted-foreground"
        />
      ) : (
        <>
          <BubbleButton
            label="Bold"
            active={isBold}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <Bold className="h-4 w-4" />
          </BubbleButton>
          <BubbleButton
            label="Italic"
            active={isItalic}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <Italic className="h-4 w-4" />
          </BubbleButton>
          <BubbleButton
            label="Strikethrough"
            active={isStrike}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          >
            <Strikethrough className="h-4 w-4" />
          </BubbleButton>
          <BubbleButton
            label="Inline code"
            active={isCode}
            onClick={() => editor.chain().focus().toggleCode().run()}
          >
            <Code className="h-4 w-4" />
          </BubbleButton>
          <div className="mx-0.5 h-4 w-px shrink-0 bg-border" />
          <BubbleButton label="Link" active={isLink} onClick={openLinkEditor}>
            <Link2 className="h-4 w-4" />
          </BubbleButton>
          {onCommentSelection ? (
            <>
              <div className="mx-0.5 h-4 w-px shrink-0 bg-border" />
              <BubbleButton
                label="Comment"
                onClick={() => commentOnSelection(editor, onCommentSelection)}
              >
                <MessageSquarePlus className="h-4 w-4" />
              </BubbleButton>
            </>
          ) : null}
        </>
      )}
    </BubbleMenu>
  );
}

interface BubbleButtonProps {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

// Mirrors EditorToolbar's ToolbarButton: `onPointerDown` + `preventDefault`
// keeps the editor focused (and the text selection intact) when a toggle is
// pressed, so the mark applies to the highlighted span.
function BubbleButton({ label, active, onClick, children }: BubbleButtonProps) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      onPointerDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`flex shrink-0 items-center rounded px-1.5 py-1 hover:bg-muted hover:text-foreground ${
        active ? "bg-muted text-foreground" : ""
      }`}
    >
      {children}
    </button>
  );
}
